import { QRCodeSVG } from "qrcode.react";
import { Download, Printer, QrCode as QrCodeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRef } from "react";

interface QRCodeGeneratorProps {
  restaurantName: string;
  restaurantSlug: string;
  tableNumber: string;
  logoUrl?: string | null;
}

export function QRCodeGenerator({ 
  restaurantName, 
  restaurantSlug, 
  tableNumber,
  logoUrl 
}: QRCodeGeneratorProps) {
  const qrRef = useRef<HTMLDivElement>(null);
  
  // URL base do cardápio da mesa
  const menuUrl = `${window.location.origin}/menu/${restaurantSlug}/mesa/${tableNumber}`;

  const handleDownload = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const data = new XMLSerializer().serializeToString(svg);

    const img = new Image();
    const svgBlob = new Blob([data], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      // Criar canvas com padding para o template
      canvas.width = 400;
      canvas.height = 500;

      if (ctx) {
        // Fundo branco
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Título do restaurante
        ctx.fillStyle = "#1a1a1a";
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText(restaurantName, canvas.width / 2, 40);

        // Número da mesa
        ctx.font = "bold 32px Arial";
        ctx.fillText(`Mesa ${tableNumber}`, canvas.width / 2, 80);

        // QR Code centralizado
        const qrSize = 280;
        const qrX = (canvas.width - qrSize) / 2;
        ctx.drawImage(img, qrX, 100, qrSize, qrSize);

        // Texto de instrução
        ctx.font = "16px Arial";
        ctx.fillStyle = "#666";
        ctx.fillText("Escaneie para ver o cardápio", canvas.width / 2, 420);
        ctx.fillText("e fazer pedidos", canvas.width / 2, 445);

        // Download
        const pngUrl = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `qrcode-mesa-${tableNumber}.png`;
        downloadLink.href = pngUrl;
        downloadLink.click();
      }

      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - Mesa ${tableNumber}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
              padding: 20px;
              border: 2px solid #ddd;
              border-radius: 16px;
            }
            h1 {
              margin: 0 0 8px 0;
              font-size: 24px;
            }
            h2 {
              margin: 0 0 20px 0;
              font-size: 32px;
            }
            .qr-code {
              margin: 20px auto;
            }
            .instruction {
              color: #666;
              font-size: 14px;
              margin-top: 16px;
            }
            @media print {
              body { margin: 0; }
              .container { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${restaurantName}</h1>
            <h2>Mesa ${tableNumber}</h2>
            <div class="qr-code">${svgData}</div>
            <p class="instruction">Escaneie para ver o cardápio e fazer pedidos</p>
          </div>
          <script>
            window.onload = () => {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <QrCodeIcon className="h-4 w-4" />
          Mesa {tableNumber}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div ref={qrRef} className="flex justify-center p-4 bg-white rounded-lg">
          <QRCodeSVG
            value={menuUrl}
            size={160}
            level="H"
            includeMargin
            imageSettings={logoUrl ? {
              src: logoUrl,
              height: 30,
              width: 30,
              excavate: true,
            } : undefined}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Este QR é para o <span className="font-medium">celular do cliente</span> (modo mesa/QR). Tablets fixos devem usar o link do modo Tablet.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" />
            Baixar
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" />
            Imprimir
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
